(() => {
  'use strict';

  // Shopify's GET filter values remain catalog amounts; only visible controls use the offer price.
  const toMinor = value => {
    if (!/^\d+(?:\.\d{0,2})?$/.test(value)) return null;
    const [whole, fraction = ''] = value.split('.');
    return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  };
  const decimal = minor => `${minor / 100n}.${String(minor % 100n).padStart(2, '0')}`;
  const finalAmount = (catalog, boundary) => {
    const minor = toMinor(catalog);
    if (minor === null) return '';
    return decimal((minor * 65n + 99n) / 100n);
  };
  const catalogAmount = (value, boundary) => {
    const minor = toMinor(value);
    if (minor === null) return null;
    const lower = minor * 100n - 99n;
    return decimal(boundary === 'min' ? (lower > 0n ? (lower + 64n) / 65n : 0n) : minor * 100n / 65n);
  };

  function initialize(root) {
    if (root.dataset.priceFilterReady) return;
    const fields = [...root.querySelectorAll('[data-jmo-price-fieldset]')];
    if (!fields.length) return;
    let money;
    try { money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: root.dataset.currency }); }
    catch (_) { return; } // Keep the explicit native-price fallback if currency formatting is unavailable.
    root.dataset.priceFilterReady = 'true';
    let enhanced = false;
    let timer;
    const pairs = fields.flatMap(field => [...field.querySelectorAll('[data-jmo-price-input]')].map(input => ({ input, hidden: null, name: input.name, maximum: input.max, placeholder: input.placeholder })));
    const chips = [...root.querySelectorAll('[data-jmo-price-chip]')].map(element => ({ element, original: element.textContent }));
    const hints = fields.map(field => ({ field, unit: field.querySelector('[data-jmo-price-unit]'), hint: field.querySelector('[data-jmo-price-hint]'), original: field.querySelector('[data-jmo-price-hint]').textContent }));
    const eligible = () => root.dataset.promotionActive === 'true' && Date.now() < Number(root.dataset.promotionEndsAt);
    const sync = pair => {
      if (!enhanced) return;
      if (pair.input.value === pair.visibleValue) {
        pair.input.setCustomValidity('');
        return;
      }
      const value = pair.input.value === '' ? '' : catalogAmount(pair.input.value, pair.input.dataset.jmoPriceInput);
      pair.input.setCustomValidity(value === null ? 'Enter a price with no more than two decimal places.' : '');
      if (value !== null) {
        pair.hidden.value = value;
        pair.visibleValue = pair.input.value;
      }
    };
    const refresh = () => {
      window.clearTimeout(timer);
      const active = eligible();
      if (active && !enhanced) {
        pairs.forEach(pair => {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = pair.name;
          hidden.value = pair.input.value;
          pair.hidden = hidden;
          pair.input.insertAdjacentElement('afterend', hidden);
          pair.input.removeAttribute('name');
          pair.input.value = pair.input.value === '' ? '' : finalAmount(pair.input.value, pair.input.dataset.jmoPriceInput);
          pair.visibleValue = pair.input.value;
          pair.input.max = finalAmount(pair.maximum, 'max');
          pair.input.placeholder = finalAmount(pair.placeholder, 'max');
        });
        enhanced = true;
      } else if (!active && enhanced) {
        pairs.forEach(pair => {
          pair.input.value = pair.hidden.value;
          pair.input.name = pair.name;
          pair.input.max = pair.maximum;
          pair.input.placeholder = pair.placeholder;
          pair.input.setCustomValidity('');
          pair.hidden.remove();
          pair.hidden = null;
        });
        enhanced = false;
      }
      hints.forEach(({ unit, hint, original }) => {
        unit.textContent = active ? `Prices after 35% off · ${root.dataset.currency}` : `Prices in ${root.dataset.currency}.`;
        hint.textContent = active ? `Maximum ${money.format(Math.ceil(Number(hint.dataset.catalogMaximum) * 65 / 100) / 100)} after 35% off.` : original;
      });
      chips.forEach(({ element, original }) => {
        element.textContent = active ? `${money.format(Math.ceil(Number(element.dataset.catalogMinimum) * 65 / 100) / 100)}–${money.format(Math.ceil(Number(element.dataset.catalogMaximum) * 65 / 100) / 100)} after 35% off` : original.replace(/ before promotion$/, '');
      });
      if (active) timer = window.setTimeout(refresh, Math.min(Number(root.dataset.promotionEndsAt) - Date.now() + 20, 2147483647));
    };
    pairs.forEach(pair => {
      pair.input.addEventListener('input', () => sync(pair));
      pair.input.addEventListener('change', () => sync(pair));
    });
    root.querySelector('.jmo-catalog__controls')?.addEventListener('submit', event => {
      pairs.forEach(sync);
      refresh();
      if (pairs.some(pair => !pair.input.checkValidity())) {
        event.preventDefault();
        pairs.find(pair => !pair.input.checkValidity())?.input.reportValidity();
      }
    });
    refresh();
    document.addEventListener('jmo:promotion-expired', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', refresh);
  }
  const initializeAll = () => document.querySelectorAll('[data-jmo-price-filter]').forEach(initialize);
  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
